import { useState } from 'react'
import { Search, RefreshCw, ArrowUpDown, Archive, Tag, Download, Trash2, X, Upload } from 'lucide-react'

export default function ArchiveList({
  files,
  selected,
  loading,
  searchTerm,
  onSearch,
  onSelect,
  onRefresh,
  allTags,
  activeTag,
  onTagFilter,
  checked,
  onToggle,
  onToggleAll,
  onClearSelection,
  onBulkTag,
  onBulkDownload,
  onBulkRestore,
  onBulkDelete
}) {
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState('asc')

  function toggleSort(field) {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDir(field === 'name' ? 'asc' : 'desc')
    }
  }

  const sorted = [...files].sort((a, b) => {
    let cmp = 0
    if (sortBy === 'name') {
      cmp = a.filename.localeCompare(b.filename)
    } else if (sortBy === 'date') {
      cmp = new Date(a.uploaded || 0) - new Date(b.uploaded || 0)
    } else if (sortBy === 'size') {
      cmp = (a.size || 0) - (b.size || 0)
    }
    return sortDir === 'asc' ? cmp : -cmp
  })

  const allChecked = sorted.length > 0 && sorted.every(f => checked.has(f.filename))
  const someChecked = checked.size > 0

  return (
    <>
      <div className="archive-list-controls">
        <div className="archive-list-search">
          <Search size={14} className="search-icon" />
          <input
            type="text"
            placeholder="Filter archive..."
            value={searchTerm}
            onChange={e => onSearch(e.target.value)}
          />
        </div>
        <div className="archive-list-tag-filter">
          <Tag size={13} />
          <select
            value={activeTag}
            onChange={e => onTagFilter(e.target.value)}
          >
            <option value="">All Tags</option>
            {(allTags || []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <button onClick={onRefresh} className="icon-btn" title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {someChecked && (
        <div className="bulk-action-bar">
          <span className="bulk-count">{checked.size} selected</span>
          <button onClick={onBulkTag}><Tag size={12} /> Tag</button>
          <button onClick={onBulkDownload}><Download size={12} /> Download</button>
          <button onClick={onBulkRestore}><Upload size={12} /> Restore</button>
          <button onClick={onBulkDelete} className="danger"><Trash2 size={12} /> Delete</button>
          <button onClick={onClearSelection} className="bulk-clear"><X size={12} /> Clear</button>
        </div>
      )}

      <div className="archive-list-table">
        <table>
          <thead>
            <tr>
              <th className="bulk-checkbox-cell">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={() => onToggleAll(sorted)}
                />
              </th>
              <th onClick={() => toggleSort('name')} className="sortable">
                Name {sortBy === 'name' && <ArrowUpDown size={10} />}
              </th>
              <th onClick={() => toggleSort('size')} className="sortable th-size">
                Size {sortBy === 'size' && <ArrowUpDown size={10} />}
              </th>
              <th onClick={() => toggleSort('date')} className="sortable th-date">
                Date {sortBy === 'date' && <ArrowUpDown size={10} />}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="table-message">Loading...</td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={4} className="table-message">
                  <Archive size={20} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <br />
                  {searchTerm || activeTag ? `No matching files${activeTag ? ` tagged "${activeTag}"` : ''}` : 'Archive is empty'}
                </td>
              </tr>
            ) : (
              sorted.map(file => (
                <tr
                  key={file.filename}
                  className={selected?.filename === file.filename ? 'selected' : ''}
                >
                  <td className="bulk-checkbox-cell" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked.has(file.filename)}
                      onChange={() => onToggle(file.filename)}
                    />
                  </td>
                  <td className="archive-filename-cell" title={file.filename} onClick={() => onSelect(file)}>
                    {file.filename}
                  </td>
                  <td className="archive-size-cell" onClick={() => onSelect(file)}>
                    {formatBytes(file.size)}
                  </td>
                  <td className="archive-date-cell" onClick={() => onSelect(file)}>
                    {file.uploaded ? formatDate(file.uploaded) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '—'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatDate(iso) {
  const d = new Date(iso)
  const now = new Date()
  const diff = now - d
  
  if (diff < 86400000) {
    const hrs = Math.floor(diff / 3600000)
    if (hrs < 1) return 'Just now'
    return `${hrs}h ago`
  }
  
  if (diff < 604800000) {
    const days = Math.floor(diff / 86400000)
    return `${days}d ago`
  }
  
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}
